/**
 * Wishlist and recently viewed, backed by localStorage.
 *
 * Both features are lists of product handles kept in the visitor's browser, so
 * they need no app, no customer account and no server. The trade-off is that a
 * list does not follow the visitor to another device. If accounts are ever
 * wanted for this, the storage functions below are the only place that would
 * change.
 *
 * Cards are rendered by asking Shopify for one product's card markup through the
 * Section Rendering API, the same approach Dawn's quick add already uses. A
 * handle whose product has been deleted or unpublished returns a 404, and is
 * pruned from storage rather than left to fail silently on every page load.
 *
 * Every localStorage access is wrapped: it throws outright in some privacy
 * modes, and an exception here would take down the rest of the page.
 */
(() => {
  const STORAGE_KEYS = {
    wishlist: 'theme:wishlist',
    recent: 'theme:recently-viewed',
  };

  const LIMITS = {
    wishlist: 50,
    recent: 12,
  };

  const CHANGE_EVENT = 'saved-products:change';

  function read(list) {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS[list]);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Guard against anything that is not a handle, including values written by
      // an older version of this script.
      return parsed.filter((handle) => typeof handle === 'string' && handle.length > 0);
    } catch (error) {
      return [];
    }
  }

  function write(list, handles) {
    try {
      localStorage.setItem(STORAGE_KEYS[list], JSON.stringify(handles.slice(0, LIMITS[list])));
    } catch (error) {
      // Storage full or blocked. The feature degrades to not remembering.
    }
  }

  function announce(list) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
  }

  /** Adds a handle, newest first, without duplicates. */
  function add(list, handle) {
    const handles = read(list).filter((existing) => existing !== handle);
    handles.unshift(handle);
    write(list, handles);
    announce(list);
  }

  function remove(list, handle) {
    write(
      list,
      read(list).filter((existing) => existing !== handle)
    );
    announce(list);
  }

  function has(list, handle) {
    return read(list).includes(handle);
  }

  // Another tab changed a list. Re-announce it here so every element on this
  // page (card hearts, the header badge, the saved-items grid) resyncs through
  // the same change event it already handles. A null key means storage was
  // cleared entirely.
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === STORAGE_KEYS.wishlist) announce('wishlist');
  });

  const SavedProducts = { read, add, remove, has, CHANGE_EVENT };
  window.SavedProducts = SavedProducts;

  function rootUrl() {
    // Locale-aware root, e.g. /en-gb/ on an international market. Falls back to
    // the domain root if the theme did not expose it.
    const root = window.routes && window.routes.root_url ? window.routes.root_url : '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /** Fetches one product's card markup, or null if the product is gone. */
  async function fetchCard(handle, sectionId) {
    const url = `${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`;

    let response;
    try {
      response = await fetch(url);
    } catch (error) {
      return null;
    }

    if (!response.ok) return { missing: response.status === 404 };

    const html = await response.text();
    const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-card]');
    return card ? { card } : null;
  }

  // Cards already fetched during this page view, keyed by section and handle.
  // The recently viewed row re-renders when recommendations load, and would
  // otherwise refetch every card each time. Failures are not cached, so a
  // network blip is retried on the next render.
  const cardCache = new Map();

  function getCard(handle, sectionId) {
    const key = `${sectionId}:${handle}`;
    if (!cardCache.has(key)) {
      cardCache.set(
        key,
        fetchCard(handle, sectionId).then((result) => {
          if (!result || !result.card) cardCache.delete(key);
          return result;
        })
      );
    }
    return cardCache.get(key);
  }

  /**
   * Handles of products already shown as product cards on the page, ignoring
   * any inside `root`. Reads the card title link, which every card-product
   * render has, and copes with locale prefixes and /collections/x/products/y.
   */
  function handlesShownOutside(root) {
    const handles = new Set();
    document.querySelectorAll('.card__heading a[href]').forEach((link) => {
      if (root.contains(link)) return;
      const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
      if (match) handles.add(decodeURIComponent(match[1]));
    });
    return handles;
  }

  /**
   * Records the current product as viewed.
   * Rendered on the product template as <product-view-tracker data-product-handle="...">.
   */
  class ProductViewTracker extends HTMLElement {
    connectedCallback() {
      const handle = this.dataset.productHandle;
      if (handle) add('recent', handle);
    }
  }
  if (!customElements.get('product-view-tracker')) customElements.define('product-view-tracker', ProductViewTracker);

  /**
   * Renders a saved list as product cards.
   * <saved-products data-list="wishlist|recent" data-section-id="..." data-limit="4" data-hide-shown>
   *
   * With data-hide-shown, products already shown as cards elsewhere on the page
   * are left out, and older history fills the gap. On a small catalogue that
   * can hide the row entirely; it reappears once there is something new to show.
   */
  class SavedProductsList extends HTMLElement {
    connectedCallback() {
      this.list = this.dataset.list;
      this.sectionId = this.dataset.sectionId;
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.exclude = this.dataset.exclude || '';
      this.hideShown = this.hasAttribute('data-hide-shown');
      this.renderId = 0;
      this.grid = this.querySelector('[data-saved-grid]');
      this.empty = this.querySelector('[data-saved-empty]');

      this.onChange = this.onChange.bind(this);
      // Only the wishlist changes while its own page is open. Re-rendering the
      // recently viewed row on every product view would fight the page.
      if (this.list === 'wishlist') document.addEventListener(CHANGE_EVENT, this.onChange);

      // Dawn's recommendations ("You may also like", "Pairs well with") load after
      // the page, once scrolled near, and fire no event when they do. Watch them so
      // any products they add are dropped from this row. They sit above this row,
      // so they normally load before it scrolls into view.
      if (this.hideShown) {
        this.scheduleRender = this.scheduleRender.bind(this);
        this.observer = new MutationObserver(this.scheduleRender);
        document.querySelectorAll('product-recommendations').forEach((element) => {
          this.observer.observe(element, { childList: true, subtree: true });
        });
      }

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.renderTimer);
    }

    onChange(event) {
      if (event.detail.list === this.list) this.render();
    }

    /** Coalesces the burst of mutations a recommendations load produces. */
    scheduleRender() {
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.render(), 50);
    }

    async render() {
      const renderId = ++this.renderId;
      const shown = this.hideShown ? handlesShownOutside(this) : new Set();

      // Filter before limiting, so older history fills any slots freed up.
      const handles = read(this.list)
        .filter((handle) => handle !== this.exclude && !shown.has(handle))
        .slice(0, this.limit);

      if (handles.length === 0) {
        this.setEmpty(true);
        return;
      }

      const results = await Promise.all(handles.map((handle) => getCard(handle, this.sectionId)));

      // A newer render started while this one was fetching. Let that one win.
      if (renderId !== this.renderId) return;

      const missing = [];
      const cards = [];
      results.forEach((result, index) => {
        if (result && result.card) {
          cards.push(result.card);
        } else if (result && result.missing) {
          missing.push(handles[index]);
        }
      });

      // Drop handles whose products no longer resolve, so they are not refetched.
      if (missing.length > 0) {
        write(
          this.list,
          read(this.list).filter((handle) => !missing.includes(handle))
        );
      }

      if (cards.length === 0) {
        this.setEmpty(true);
        return;
      }

      this.grid.replaceChildren(...cards.map((card) => this.wrap(card.cloneNode(true))));
      this.setEmpty(false);
    }

    /** Matches Dawn's grid markup so the cards inherit the usual column sizing. */
    wrap(card) {
      const item = document.createElement('li');
      item.className = 'grid__item';
      item.appendChild(card);
      return item;
    }

    setEmpty(isEmpty) {
      if (this.empty) this.empty.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
      // Drop cards from an earlier render rather than leaving them hidden in the
      // page, where their save buttons would keep listening for changes.
      if (isEmpty && this.grid) this.grid.replaceChildren();
      // The whole section hides when there is nothing to show and no empty state,
      // which is what the recently viewed row wants.
      if (isEmpty && !this.empty) this.hidden = true;
      else if (!isEmpty) this.hidden = false;
    }
  }
  if (!customElements.get('saved-products')) customElements.define('saved-products', SavedProductsList);

  /**
   * Toggles one product in the wishlist.
   * <wishlist-button data-product-handle="..."><button …></wishlist-button>
   */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('button');
      if (!this.handle || !this.button) return;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.onChange);

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    onClick(event) {
      // Cards wrap the whole tile in a link; without this the click navigates.
      event.preventDefault();
      event.stopPropagation();

      if (has('wishlist', this.handle)) remove('wishlist', this.handle);
      else add('wishlist', this.handle);
    }

    onChange(event) {
      if (event.detail.list === 'wishlist') this.sync();
    }

    sync() {
      const saved = has('wishlist', this.handle);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.classList.toggle('wishlist-button--saved', saved);

      const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }
  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

  /**
   * Shows how many items are saved, for the header icon.
   * <wishlist-count data-label-one="[count] saved item" data-label-other="[count] saved items">
   *
   * Stays in step across tabs through the storage bridge above. Handles for
   * products deleted from the store still count until the wishlist page next
   * loads and prunes them.
   */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    onChange(event) {
      if (event.detail.list === 'wishlist') this.sync();
    }


    sync() {
      const count = read('wishlist').length;
      this.hidden = count === 0;
      this.replaceChildren();
      if (count === 0) return;

      // Matches the cart bubble: the number is decorative and hidden past 99,
      // while the full count is always available to screen readers. Only the
      // one/other plural forms are handled; the Liquid translation supplies both.
      if (count < 100) {
        const number = document.createElement('span');
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(count);
        this.append(number);
      }

      const template = count === 1 ? this.dataset.labelOne : this.dataset.labelOther;
      if (template) {
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = template.replace('[count]', String(count));
        this.append(label);
      }
    }
  }
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
})();
